import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { checkAdminExists as checkAdmin, getCurrentUser, signOut, User } from '../lib/auth';
import { useLanguage } from '../context/LanguageContext';

/** EN / FIL pill toggle shown in both desktop and mobile nav */
function LangToggle() {
  const { lang, setLang } = useLanguage();
  return (
    <div className="flex items-center bg-slate-800 rounded-full p-0.5 border border-slate-700/60 select-none">
      <button
        onClick={() => setLang('en')}
        className={`px-2.5 py-0.5 rounded-full text-xs font-semibold transition-all ${
          lang === 'en'
            ? 'bg-blue-600 text-white shadow'
            : 'text-slate-400 hover:text-white'
        }`}
        aria-label="Switch to English"
      >
        EN
      </button>
      <button
        onClick={() => setLang('fil')}
        className={`px-2.5 py-0.5 rounded-full text-xs font-semibold transition-all ${
          lang === 'fil'
            ? 'bg-blue-600 text-white shadow'
            : 'text-slate-400 hover:text-white'
        }`}
        aria-label="Lumipat sa Filipino"
      >
        FIL
      </button>
    </div>
  );
}

export default function Navbar() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [showAdminSetup, setShowAdminSetup] = useState(false);
  const location = useLocation();

  useEffect(() => {
    checkAdminExists();
    loadUser();
  }, [location.pathname]);

  const loadUser = async () => {
    try {
      const currentUser = await getCurrentUser();
      setUser(currentUser);
    } catch (err) {
      console.error('Failed to load user:', err);
    }
  };

  const checkAdminExists = async () => {
    try {
      const exists = await checkAdmin();
      setShowAdminSetup(!exists);
    } catch (err) {
      console.error('Failed to check admin status:', err);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut();
      setUser(null);
      navigate('/');
    } catch (err) {
      console.error('Logout failed:', err);
    }
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-900/80 backdrop-blur-md border-b border-slate-700/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center group">
              <div className="relative">
                <img
                  src="/pwa-192x192.png"
                  alt="Agos Logo"
                  className="h-8 w-8 object-contain group-hover:scale-110 transition-transform"
                />
              </div>
              <span className="ml-2 text-xl font-bold text-white flex items-center gap-2">
                Agos Identity
                <span className="px-1.5 py-0.5 rounded text-[10px] font-bold tracking-wider uppercase border border-blue-500/50 bg-blue-500/10 text-blue-400">
                  BETA
                </span>
              </span>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-6">
            <Link
              to="/pricing"
              className={`text-slate-300 hover:text-white transition-colors text-sm ${
                location.pathname === '/pricing' ? 'text-white font-semibold' : ''
              }`}
            >
              {t('nav_pricing')}
            </Link>
            <Link
              to="/verify"
              className={`text-slate-300 hover:text-white transition-colors text-sm ${
                location.pathname === '/verify' ? 'text-white font-semibold' : ''
              }`}
            >
              {t('nav_verify')}
            </Link>
            <Link
              to="/faq"
              className="text-slate-300 hover:text-white transition-colors text-sm"
            >
              FAQ
            </Link>

            {/* Language toggle */}
            <LangToggle />

            {user ? (
              <>
                <Link
                  to="/dashboard"
                  className={`text-slate-300 hover:text-white transition-colors text-sm ${
                    location.pathname === '/dashboard' ? 'text-white font-semibold' : ''
                  }`}
                >
                  {t('nav_dashboard')}
                </Link>
                <button
                  onClick={handleLogout}
                  className="text-slate-300 hover:text-white transition-colors text-sm"
                >
                  {t('nav_logout')}
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className={`text-slate-300 hover:text-white transition-colors text-sm ${
                    location.pathname === '/login' ? 'text-white font-semibold' : ''
                  }`}
                >
                  {t('nav_login')}
                </Link>
                <Link
                  to="/register"
                  className="bg-gradient-to-r from-blue-600 to-blue-500 text-white px-5 py-2 rounded-lg hover:from-blue-500 hover:to-blue-400 transition-all shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 text-sm font-medium"
                >
                  {t('nav_register')}
                </Link>
              </>
            )}
          </div>

          {/* Mobile: lang toggle + hamburger */}
          <div className="md:hidden flex items-center gap-3">
            <LangToggle />
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-slate-300 hover:text-white focus:outline-none transition-colors"
              aria-expanded={isOpen}
              aria-label="Toggle navigation menu"
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="md:hidden bg-slate-900/95 backdrop-blur-md border-t border-slate-700/50">
            <div className="pt-2 pb-3 space-y-1">
              <Link
                to="/pricing"
                className="block px-3 py-2 text-slate-300 hover:text-white hover:bg-slate-800/50 rounded-lg transition-colors"
                onClick={() => setIsOpen(false)}
              >
                {t('nav_pricing')}
              </Link>
              <Link
                to="/verify"
                className="block px-3 py-2 text-slate-300 hover:text-white hover:bg-slate-800/50 rounded-lg transition-colors"
                onClick={() => setIsOpen(false)}
              >
                {t('nav_verify')}
              </Link>
              <Link
                to="/faq"
                className="block px-3 py-2 text-slate-300 hover:text-white hover:bg-slate-800/50 rounded-lg transition-colors"
                onClick={() => setIsOpen(false)}
              >
                FAQ
              </Link>
              {user ? (
                <>
                  <Link
                    to="/dashboard"
                    className="block px-3 py-2 text-slate-300 hover:text-white hover:bg-slate-800/50 rounded-lg transition-colors"
                    onClick={() => setIsOpen(false)}
                  >
                    {t('nav_dashboard')}
                  </Link>
                  <button
                    onClick={() => { handleLogout(); setIsOpen(false); }}
                    className="block w-full text-left px-3 py-2 text-slate-300 hover:text-white hover:bg-slate-800/50 rounded-lg transition-colors"
                  >
                    {t('nav_logout')}
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="block px-3 py-2 text-slate-300 hover:text-white hover:bg-slate-800/50 rounded-lg transition-colors"
                    onClick={() => setIsOpen(false)}
                  >
                    {t('nav_login')}
                  </Link>
                  <Link
                    to="/register"
                    className="block mx-3 py-2 bg-gradient-to-r from-blue-600 to-blue-500 text-white text-center rounded-lg hover:from-blue-500 hover:to-blue-400 transition-all font-medium"
                    onClick={() => setIsOpen(false)}
                  >
                    {t('nav_register')}
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
